import { ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { AppConfig } from '@luggagehero/shared/app-settings/data-access';
import { FeatureResolver } from '@luggagehero/shared/feature-resolution';
import {
  BookableStorageLocation,
  IPricing,
  PRICING_SERVICE,
  PricingService,
  StorageCriteria,
  TRANSLATE_SERVICE,
} from '@luggagehero/shared/interfaces';
import { SharedIntercomService } from '@luggagehero/shared/services/intercom';
import { SharedShopsService } from '@luggagehero/shared/services/shops';
import { SharedStorageCriteriaService } from '@luggagehero/shared/services/storage-criteria';
import { firstValueFrom, map, Observable, Subscription } from 'rxjs';

import { BaseModalComponent } from '../../../core';

@Component({
  template: '',
  standalone: false,
})
export abstract class PricingBaseComponent extends BaseModalComponent implements OnInit, OnDestroy {
  public showFullExplanation = false;
  private shopsService = inject(SharedShopsService);
  private pricingService = inject<PricingService>(PRICING_SERVICE);
  private translateService = inject(TRANSLATE_SERVICE);
  private featureResolver = inject(FeatureResolver);
  private criteriaService = inject(SharedStorageCriteriaService);
  private intercom = inject(SharedIntercomService);
  private cd = inject(ChangeDetectorRef);
  private _criteria: StorageCriteria;
  private _shop: BookableStorageLocation;
  private _isDailyRateDefault = false;
  private _isInitialized = false;
  private subscriptions: Subscription[] = [];

  public get shop(): BookableStorageLocation {
    return this._shop;
  }
  @Input() public set shop(value: BookableStorageLocation) {
    this._shop = value;
    this.cd.markForCheck();
  }

  public get criteria(): StorageCriteria {
    return this._criteria;
  }
  public set criteria(value: StorageCriteria) {
    this._criteria = value;
    this.cd.markForCheck();
  }

  public get pricing(): IPricing {
    return this.shop?.pricing;
  }

  public get region(): string {
    return this.shopsService.latestPrice?.region;
  }

  public get insuranceCoverage(): number {
    return this.shop?.pricing?.insuranceCoverage || this.shopsService.latestPrice?.pricings[0]?.insuranceCoverage;
  }

  public get hourlyRateHtml(): Observable<string> {
    return this.pricingService.transformPricing(this.pricing, false, false, true, false, false, true).pipe(
      map((html) => html?.replace('>/', '>') ?? ''), // HACK: Remove "/" from "/hour"
    );
  }

  public get dailyRateHtml(): Observable<string> {
    return this.pricingService.transformPricing(this.pricing, false, true, false, false, false, true).pipe(
      map((html) => html?.replace('>/', '>') ?? ''), // HACK: Remove "/" from "/day"
    );
  }

  public get serviceFeeFootnote(): string {
    // Gated by app settings (global switch or per-city allowlist); off by default.
    if (!this.featureResolver.showServiceFeeFootnote(this.shop)) {
      return '';
    }
    // Sourced from the shop's productList (same pricing as issued orders),
    // not the legacy `IPricing.serviceFee`.
    const serviceFee = this.shopsService.getServiceFeeForShop(this.shop);
    if (!serviceFee) {
      return '';
    }
    return this.translateService.instant('PLUS_SERVICE_FEE', undefined, {
      fee: this.pricingService.format(serviceFee.cost, serviceFee.currency),
    });
  }

  public get isDailyRateDefault(): boolean {
    return this._isDailyRateDefault;
  }

  public get isInitialized(): boolean {
    return this._isInitialized;
  }

  public ngOnInit() {
    this.criteria = this.criteriaService.currentOrDefault;
    this.subscriptions.push(this.criteriaService.change.subscribe((c) => (this.criteria = c)));

    void firstValueFrom(this.featureResolver.isDailyPricingDefault$).then((isEnabled) => {
      console.log('Daily pricing by default:', isEnabled);
      this._isDailyRateDefault = isEnabled;
      this._isInitialized = true;
      this.cd.markForCheck();
    });
  }

  public ngOnDestroy(): void {
    try {
      if (this.subscriptions) {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
      }
    } catch {
      // Ignore
    }
  }

  public showContact() {
    if (AppConfig.IS_CHAT_ON_CONTACT_LINK_ENABLED) {
      this.intercom.show();
    }
  }
}
